import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { formatDateTime } from '../../../config/appConfig';
import {
  submitterDisplayName,
  useEFormSubmissions,
  usePublishedEForms,
  type EFormSubmission,
} from '../../e-forms/useData';
import GenericListWidget from './GenericListWidget';

function parseDefinitionId(...candidates: unknown[]): number | null {
  for (const raw of candidates) {
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
    if (typeof raw === 'string' && raw.trim()) {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
    }
    if (raw && typeof raw === 'object' && 'definitionId' in raw) {
      const nested = parseDefinitionId((raw as { definitionId?: unknown }).definitionId);
      if (nested != null) return nested;
    }
  }
  return null;
}

const EformsRecentSubmissions: React.FC<{
  limit?: number;
  embedded?: boolean;
  definitionId?: number | string;
  settings?: Record<string, unknown>;
}> = ({ limit = 5, embedded, definitionId: definitionIdProp, settings }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const effectiveLimit = typeof settings?.limit === 'number' ? settings.limit : limit;
  const settingsDefinitionId = parseDefinitionId(definitionIdProp, settings);

  const { data: published, isLoading: publishedLoading } = usePublishedEForms(
    undefined,
    1,
    50,
    undefined,
    settingsDefinitionId == null,
  );

  const resolvedDefinitionId = useMemo(() => {
    if (settingsDefinitionId != null) return settingsDefinitionId;
    // Central dashboard only: pick a published form when no explicit filter is set.
    const list = published ?? [];
    const withSubs = list.find((d) => (d._count?.submissions ?? 0) > 0);
    return (withSubs ?? list[0])?.id ?? null;
  }, [settingsDefinitionId, published]);

  const { data: submissions, isLoading: submissionsLoading } = useEFormSubmissions(
    resolvedDefinitionId,
    undefined,
    1,
    Math.max(effectiveLimit, 25),
  );

  const isLoading =
    (settingsDefinitionId == null && publishedLoading) ||
    (resolvedDefinitionId != null && submissionsLoading);
  const items = resolvedDefinitionId == null ? [] : submissions ?? [];

  return (
    <GenericListWidget<EFormSubmission>
      title="E-Forms · Recent submissions"
      viewAllPath={
        resolvedDefinitionId
          ? `/e-forms/${resolvedDefinitionId}/submissions`
          : '/e-forms/submissions'
      }
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage={
        resolvedDefinitionId == null ? 'No published forms' : 'No submissions'
      }
      getItemKey={(item) => item.id}
      embedded={embedded}
      renderItem={(submission) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/e-forms/submissions/${submission.id}`)}
          onKeyDown={(e) =>
            e.key === 'Enter' && navigate(`/e-forms/submissions/${submission.id}`)
          }
          className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer"
        >
          <div className="min-w-0">
            <p className={`text-sm font-medium truncate ${colors.text}`}>
              {submission.definition?.name || `Form #${submission.definitionId}`}
            </p>
            <p className={`text-xs ${colors.textMuted}`}>{submitterDisplayName(submission)}</p>
          </div>
          <span className={`text-xs whitespace-nowrap ${colors.textMuted}`}>
            {formatDateTime(submission.submittedAt || submission.createdAt)}
          </span>
        </div>
      )}
    />
  );
};

export default EformsRecentSubmissions;
